//documentManager.ts
import { ErrorCallbackFunction, deleteVoid, getJson, postJson, postObject, putVoid, getBaseUri } from "../ChoiceApi";
import { ISearchDocumentFolderInputs, ISearchDocumentFolderResults, ISearchDocumentTypeInputs, ISearchDocumentTypeResults } from "../Models/SearchModel";
import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';

export async function getMenuItems(menuNameId: MenuNameIds, entityId: number | undefined): Promise<Array<IDocumentMenuStructureItemModel>> {
    let url = `api/DocumentManager/MenuHierarchy/${entityId}/${menuNameId}`;
    if (menuNameId === MenuNameIds.Practice)
        url = "api/DocumentManager/MenuHierarchy/Practice";

    return await getJson(url) as Array<IDocumentMenuStructureItemModel>;
}

// Get a single menu item
export async function getMenuItem(menuID: number, entityId: number) {
    return getJson(`api/DocumentManager/MenuHierarchy/${entityId}/MenuItem/${menuID}`);
}

// Get the documents/files for a menu item and its children
//  Specified entityId for non-Practice menu items
export async function menuItemsDocuments(baseMenuId: number, entityId: number) {
    if (entityId)
        return getJson(`api/DocumentManager/List/Menu/${baseMenuId}/${entityId}`);
    else
        return getJson(`api/DocumentManager/List/Menu/${baseMenuId}`);
}

// Get the documents/files for a lab order
export async function listOrderDocuments(orderId: number) {
    return getJson(`api/DocumentManager/List/Order/${orderId}`);
}

// Get the thumbnail for a file/document
export async function thumbnail(imageId: number, errorCallback?: ErrorCallbackFunction): Promise<IBase64> {
    return await getJson(`api/DocumentManager/Thumbnail/Base64/${imageId}`, errorCallback) as IBase64;
}

// Get the base 64 data for a file/document
export async function getDocumentBase64(imageId: number) {
    return getJson(`api/DocumentManager/Document/${imageId}/Base64`);
}

export function mimeFileUrl(imageId: number) {
    return getBaseUri() + `api/DocumentManager/Document/${imageId}/Mime`;
}

// Link a document/file to a lab order
export async function linkToOrder(imageId: number, orderId: number, errorCallback?: ErrorCallbackFunction): Promise<boolean> {
    const errMsg = await putVoid(`api/DocumentManager/Document/${imageId}/Link/Order/${orderId}`, { errorCallback });
    if (!errMsg)
        return true;

    alert(`Error trying to link document to order: ${errMsg}`);
    return false;
}

// Remove the link between a document/file and an image group
export async function removeLinkFromImageGroup(imageId: number, imageGroupId: number) {
    const errMsg = await deleteVoid(`api/DocumentManager/Document/${imageId}/Removelink/ImageGroup/${imageGroupId}`);
    if (!errMsg)
        return true;

    alert(`Error trying to un-link document from order: ${errMsg}`);
    return false;
}

// Save a document/file
export async function saveDocument(payload: IDocumentSaveInputs) {
    return postJson("api/DocumentManager/SaveDocument", payload);
}

// Delete a document/file
export async function deleteDocument(imageId: number, menuNameId?: MenuNameIds | number) {
    return deleteVoid(`api/DocumentManager/Document/${imageId}/Delete/${menuNameId}`);
}

export async function searchFolder(payload: ISearchDocumentFolderInputs): Promise<ISearchDocumentFolderResults> {
    return (await postJson("api/eDocument/SearchFolder", payload)) as ISearchDocumentFolderResults;
}

export async function searchDocumentTypes(payload: ISearchDocumentTypeInputs): Promise<ISearchDocumentTypeResults> {
    return (await postJson("api/eDocument/SearchDocumentType", payload)) as ISearchDocumentTypeResults;
}

// Search documents
export async function searchDocuments(idType: DatabaseIdTypes, id: number|string, payload?: ISearchDocumentRequest)
    : Promise<ISearchDocumentResult[]|undefined>
{
    return await postObject<ISearchDocumentResult[]>(`api/DocumentManager/Document/List/${idType}/${id}`, payload);
}


// ------------------------------------------------
export enum MenuNameIds {
    Practice = 0,
    Patient = 1,
    Resource = 2,
    Payer = 3
}

export enum DocumentMenuLevel {
    Cabnet = 1,
    Draw = 2,
    Folder = 3
}

export enum DocumentLocationTypes {
    Patient = "Patient",
    Company = "Company"
}

export enum CommunicationUserTypes {
    User,       // Indicates we're associating with: users_id
    Group       // Indicates we're associating with: user_group_id
}

// ------------------------------------------------
export interface IDocumentMenuStructureItemModel {
    menu_id?: number,
    menu_description?: string,
    menu_level?: DocumentMenuLevel,
    menu_name_id?: MenuNameIds
    menu_order?: number,
    globalMenuOrder?: number
    image_count?: number
}

export class DocumentMenuItem implements IDocumentMenuStructureItemModel//, IHeirItem<any>
{
    public menu_id?: number = undefined;
    public menu_description?: string;
    public menu_level?: DocumentMenuLevel;
    public menu_name_id?: MenuNameIds;
    public menu_order?: number;
    public globalMenuOrder?: number;
    public image_count?: number;

    public name?: string;
    public children?: Array<DocumentMenuItem>;

    public constructor(args: any | IDocumentMenuStructureItemModel | DocumentMenuItem) {
        this.menu_id = args.menu_id;
        this.menu_description = args.menu_description;
        this.menu_level = args.menu_level;
        this.menu_name_id = args.menu_name_id;
        this.menu_order = args.menu_order;
        this.globalMenuOrder = args.globalMenuOrder;
        this.image_count = args.image_count;

        this.name = args.menu_description;
        this.children = args.children;
    }
}

export interface IBase64 {
    base64: string,
    mimeType: string,
    fileName: string,
    fileExt: string,
    applicationTypeDesc: string
}

export interface IDocumentSaveInputs {
    locationType: DocumentLocationTypes,
    locationId: number,
    documentName: string,
    base64Data: string,
    documentComment?: string,
    folderId?: number,                                  // Alias of menu_id
    communicationUserType?: CommunicationUserTypes,     // Used when we want to attach a document to a communication
    communicationUserId?: number                        // See CommunicationUserTypes for associated value ID
}

export interface ISearchDocumentRequest {
    fileName?: string,
    createDate?: string,
    cabinetName?: string,
    drawerName?: string,
    folderName?: string,
    daysSinceCreate?: number
}

export interface ISearchDocumentResult {
    imageId: number,
    fileName: string,
    createDate: string,
    fileSizeInBytes?: number,
    cabinetId?: number,
    cabinetName?: string,
    drawerId?: number,
    drawerName?: string,
    folderId: number,
    folderName: string
};
